import React, { useEffect, useState } from "react";
import api, { formatIDR, formatDate, API } from "@/lib/api";
import { DateRangeFilter, firstOfMonth, todayStr, buildDateQuery } from "@/components/DateRangeFilter";
import { ProjectCombobox } from "@/components/ProjectCombobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, PaperPlaneTilt, CheckCircle, XCircle, Eye, Printer, FilePdf, FileXls, Robot } from "@phosphor-icons/react";
import { toast } from "sonner";
import { usePaginated } from "@/components/Paginator";
import { Checkbox } from "@/components/ui/checkbox";

const today = () => new Date().toISOString().slice(0, 10);

const STATUS_BADGE = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-700" },
  pending_approval: { label: "Menunggu Persetujuan", cls: "bg-amber-100 text-amber-700" },
  approved: { label: "Disetujui & Tereksekusi", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Ditolak", cls: "bg-rose-100 text-rose-700" },
};

export default function FundRequests() {
  const [rows, setRows] = useState([]);
  const [cashBanks, setCashBanks] = useState([]);
  const [expenseAccs, setExpenseAccs] = useState([]);
  const [unpaidInvs, setUnpaidInvs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [approveRow, setApproveRow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkDlg, setBulkDlg] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState("");
  const [reviewDlg, setReviewDlg] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewData, setReviewData] = useState({ proposals: [], total_draft: 0, reviewed_items: 0 });
  const [applying, setApplying] = useState({});

  const [form, setForm] = useState({ date: today(), cash_bank_id: "", notes: "", items: [] });

  const [dateRange, setDateRange] = useState({ from: firstOfMonth(), to: todayStr() });
  const load = () => {
    api.get(`/fund-requests${buildDateQuery(dateRange)}`).then(r => setRows(r.data));
    api.get("/cashbank/accounts").then(r => setCashBanks(r.data));
    api.get("/accounting/accounts").then(r => setExpenseAccs(r.data.filter(a => a.type === "expense" && !a.is_parent)));
    api.get("/fund-requests/unpaid-purchase-invoices").then(r => setUnpaidInvs(r.data)).catch(() => {});
    api.get("/projects?active_only=true").then(r => setProjects(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [dateRange]);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const pg = usePaginated(rows);

  const downloadFr = async (r, fmt) => {
    try {
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/fund-requests/${r.id}/${fmt}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Gagal membuat dokumen");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `pengajuan_${r.number}.${fmt === "pdf" ? "pdf" : "xlsx"}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Dokumen ${r.number} diunduh`);
    } catch (e) { toast.error(e.message); }
  };
  const printFr = async (r) => {
    try {
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/fund-requests/${r.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Gagal membuat PDF");
      const blob = await resp.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) { toast.error(e.message); }
  };

  const openNew = () => {
    setEditRow(null);
    setForm({ date: today(), cash_bank_id: cashBanks[0]?.id || "", notes: "", items: [] });
    setDlgOpen(true);
  };
  const openEdit = (r) => {
    setEditRow(r);
    setForm({ date: r.date, cash_bank_id: r.cash_bank_id, notes: r.notes || "", items: r.items.map(i => ({ ...i })) });
    setDlgOpen(true);
  };

  const addInvoiceItem = (invId) => {
    const inv = unpaidInvs.find(i => i.id === invId);
    if (!inv) return;
    if (form.items.some(i => i.invoice_id === invId)) { toast.error("Faktur ini sudah ada di daftar"); return; }
    setForm({ ...form, items: [...form.items, {
      type: "purchase_invoice", invoice_id: inv.id, invoice_number: inv.number,
      partner_id: inv.partner_id, partner_name: inv.partner_name,
      description: `Pembayaran Faktur ${inv.number} - ${inv.partner_name}`,
      account_code: "", account_name: "", amount: inv.balance, pph23_withheld: 0,
    }]});
  };
  const addExpenseItem = () => {
    setForm({ ...form, items: [...form.items, {
      type: "expense", invoice_id: "", description: "",
      account_code: expenseAccs[0]?.code || "6000", account_name: expenseAccs[0]?.name || "",
      amount: 0, partner_id: "", partner_name: "",
      project_id: "", project_name: "", pph23_withheld: 0,
    }]});
  };
  const setItem = (idx, patch) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], ...patch };
    setForm({ ...form, items });
  };
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const totalAmount = form.items.reduce((s, i) => s + Number(i.amount || 0), 0);

  const save = async () => {
    if (!form.cash_bank_id) { toast.error("Pilih sumber dana (Kas/Bank)"); return; }
    if (form.items.length === 0) { toast.error("Tambahkan minimal 1 item"); return; }
    setBusy(true);
    try {
      const payload = { ...form, items: form.items.map(i => ({ ...i, amount: Number(i.amount || 0), pph23_withheld: Number(i.pph23_withheld || 0) })) };
      if (editRow) await api.put(`/fund-requests/${editRow.id}`, payload);
      else await api.post("/fund-requests", payload);
      toast.success(editRow ? "Pengajuan diperbarui" : "Pengajuan dana tersimpan sebagai draft");
      setDlgOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan pengajuan"); }
    finally { setBusy(false); }
  };

  const doSubmit = async (r) => {
    try { await api.post(`/fund-requests/${r.id}/submit`); toast.success(`Pengajuan ${r.number} diajukan untuk persetujuan`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengajukan"); }
  };
  const doApprove = async () => {
    if (!approveRow) return;
    setBusy(true);
    try {
      const r = await api.post(`/fund-requests/${approveRow.id}/approve`);
      toast.success(`Pengajuan ${approveRow.number} disetujui — ${r.data.execution.length} transaksi tereksekusi`, { duration: 6000 });
      (r.data.execution || []).forEach(x => toast.success(x.description, { duration: 5000 }));
      setApproveRow(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyetujui"); }
    finally { setBusy(false); }
  };
  const doReject = async (r) => {
    const reason = window.prompt(`Alasan penolakan pengajuan ${r.number}:`);
    if (reason === null) return;
    try { await api.post(`/fund-requests/${r.id}/reject`, { reason }); toast.success("Pengajuan ditolak"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menolak"); }
  };
  const doDelete = async (r) => {
    const warn = r.status === "approved"
      ? `Hapus riwayat pengajuan ${r.number} yang SUDAH DISETUJUI?\n\nPerhatian: transaksi yang sudah tereksekusi (pembayaran vendor / jurnal beban) TIDAK ikut terhapus — hanya riwayat pengajuannya.`
      : r.status === "pending_approval"
      ? `Hapus pengajuan ${r.number} yang sedang menunggu persetujuan?`
      : `Hapus pengajuan ${r.number}?`;
    if (!window.confirm(warn)) return;
    try { await api.delete(`/fund-requests/${r.id}`); toast.success("Pengajuan dihapus"); setSelected((s) => s.filter((x) => x !== r.id)); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };
  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => {
    const ids = pg.paged.map((r) => r.id);
    const allSel = ids.length > 0 && ids.every((id) => selected.includes(id));
    setSelected(allSel ? selected.filter((id) => !ids.includes(id)) : [...new Set([...selected, ...ids])]);
  };
  const bulkDelete = async () => {
    setBusy(true);
    try {
      const r = await api.post("/fund-requests/bulk-delete", { ids: selected });
      toast.success(`${r.data.deleted} riwayat pengajuan dihapus`);
      setSelected([]); setBulkDlg(false); setBulkConfirm(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
    finally { setBusy(false); }
  };

  const openReview = async () => {
    setReviewDlg(true);
    setReviewBusy(true);
    try {
      const r = await api.post("/ai/review-draft-journals", {});
      setReviewData(r.data);
      if ((r.data.proposals || []).length === 0) toast.info("AI tidak menemukan rekomendasi berbeda dari akun saat ini");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menjalankan review AI"); }
    finally { setReviewBusy(false); }
  };
  const applyProposal = async (p) => {
    const key = `${p.fr_id}::${p.item_index}`;
    setApplying((s) => ({ ...s, [key]: true }));
    try {
      await api.post("/ai/apply-account-suggestion", { fr_id: p.fr_id, item_index: p.item_index, account_code: p.suggested_code });
      toast.success(`${p.fr_number} — item "${p.description.slice(0, 30)}…" pindah ke ${p.suggested_code} ${p.suggested_name}`);
      setReviewData((d) => ({ ...d, proposals: d.proposals.filter((x) => `${x.fr_id}::${x.item_index}` !== key) }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menerapkan"); }
    finally { setApplying((s) => ({ ...s, [key]: false })); }
  };
  const doCancel = async (r) => {
    if (!window.confirm(`Batalkan pengajuan ${r.number}? Status akan kembali ke Draft sehingga bisa diedit atau dihapus.`)) return;
    try { await api.post(`/fund-requests/${r.id}/cancel`); toast.success(`Pengajuan ${r.number} dibatalkan — kembali ke Draft`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal membatalkan"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl" data-testid="fund-requests-page">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif-display text-4xl font-medium text-slate-900">Pengajuan Dana Pembayaran</h1>
          <p className="text-sm text-slate-500 mt-1">Ajukan dana untuk pembayaran invoice vendor & beban operasional. Belum terjurnal sampai disetujui — saat disetujui, transaksi sumber tereksekusi otomatis.</p>
        </div>
        {selected.length > 0 && <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => { setBulkConfirm(""); setBulkDlg(true); }} data-testid="fr-bulk-delete-btn"><Trash size={14} className="mr-1" />Hapus Terpilih ({selected.length})</Button>}
        <Button variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" onClick={openReview} data-testid="fr-review-btn" title="AI review akun untuk draft — perlu konfirmasi manual"><Robot size={14} className="mr-1" />Rekomendasi AI</Button>
        <Button className="bg-slate-900" onClick={openNew} data-testid="new-fund-request-btn"><Plus size={16} className="mr-1" />Pengajuan Baru</Button>
      </div>

      <div className="mt-4 flex justify-end"><DateRangeFilter range={dateRange} onChange={setDateRange} testidPrefix="fr" /></div>
      <div className="mt-2 bg-white border border-slate-200 rounded-md" data-testid="fr-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"><Checkbox checked={pg.paged.length > 0 && pg.paged.every((r) => selected.includes(r.id))} onCheckedChange={toggleAll} data-testid="fr-select-all" /></TableHead>
              <TableHead>Nomor</TableHead><TableHead>Tanggal</TableHead><TableHead>Pemohon</TableHead>
              <TableHead>Sumber Dana</TableHead><TableHead className="text-center">Item</TableHead>
              <TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-500 text-sm">Belum ada pengajuan dana.</TableCell></TableRow>
            ) : pg.paged.map((r) => {
              const st = STATUS_BADGE[r.status] || STATUS_BADGE.draft;
              return (
                <TableRow key={r.id} data-testid={`fr-row-${r.number}`} className={selected.includes(r.id) ? "bg-amber-50/50" : ""}>
                  <TableCell className="w-8"><Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggleSel(r.id)} data-testid={`fr-sel-${r.number}`} /></TableCell>
                  <TableCell className="font-mono-plex text-xs font-semibold">{r.number}</TableCell>
                  <TableCell>{formatDate(r.date)}</TableCell>
                  <TableCell className="text-sm">{r.created_by || "—"}</TableCell>
                  <TableCell className="text-sm">{r.cash_bank_name}</TableCell>
                  <TableCell className="text-center text-sm">{r.items?.length || 0}</TableCell>
                  <TableCell className="tabular-nums text-right font-semibold">{formatIDR(r.total_amount)}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-0.5 rounded font-semibold ${st.cls}`} data-testid={`fr-status-${r.number}`}>{st.label}</span>
                    {r.status === "rejected" && r.reject_reason && <div className="text-[11px] text-rose-600 mt-0.5">{r.reject_reason}</div>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" title="Cetak" onClick={() => printFr(r)} data-testid={`fr-print-${r.number}`}><Printer size={15} /></Button>
                      <Button variant="ghost" size="sm" title="Export PDF" className="text-rose-700" onClick={() => downloadFr(r, "pdf")} data-testid={`fr-pdf-${r.number}`}><FilePdf size={15} /></Button>
                      <Button variant="ghost" size="sm" title="Export Excel" className="text-emerald-700" onClick={() => downloadFr(r, "xlsx")} data-testid={`fr-xlsx-${r.number}`}><FileXls size={15} /></Button>
                      <Button variant="ghost" size="sm" title="Detail" onClick={() => setDetailRow(r)} data-testid={`fr-view-${r.number}`}><Eye size={15} /></Button>
                      {(r.status === "draft" || r.status === "rejected") && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`fr-edit-${r.number}`}>Edit</Button>
                          <Button variant="ghost" size="sm" className="text-sky-700" onClick={() => doSubmit(r)} data-testid={`fr-submit-${r.number}`}><PaperPlaneTilt size={15} className="mr-1" />Ajukan</Button>
                        </>
                      )}
                      <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => doDelete(r)} data-testid={`fr-del-${r.number}`} title="Hapus riwayat pengajuan"><Trash size={15} /></Button>
                      {r.status === "pending_approval" && (
                        <>
                          <Button variant="ghost" size="sm" className="text-emerald-700" onClick={() => setApproveRow(r)} data-testid={`fr-approve-${r.number}`}><CheckCircle size={15} className="mr-1" />Setujui</Button>
                          <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => doReject(r)} data-testid={`fr-reject-${r.number}`}><XCircle size={15} className="mr-1" />Tolak</Button>
                          <Button variant="ghost" size="sm" className="text-amber-700" onClick={() => doCancel(r)} data-testid={`fr-cancel-${r.number}`} title="Batalkan pengajuan — kembali ke Draft">Batalkan</Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {pg.pager}
      </div>

      {/* Dialog buat/edit */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="w-[95vw] max-w-5xl max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3"><DialogTitle>{editRow ? `Edit Pengajuan ${editRow.number}` : "Pengajuan Dana Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-4 min-h-0 overflow-y-auto px-6 pt-4 pb-6">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="fr-date-input" /></div>
              <div>
                <Label>Sumber Dana (Kas/Bank)</Label>
                <Select value={form.cash_bank_id} onValueChange={(v) => setForm({ ...form, cash_bank_id: v })}>
                  <SelectTrigger data-testid="fr-cashbank-select"><SelectValue placeholder="Pilih Kas/Bank" /></SelectTrigger>
                  <SelectContent>{cashBanks.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({formatIDR(c.balance || 0)})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Catatan</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Keperluan pengajuan…" data-testid="fr-notes-input" /></div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Item Pengajuan</Label>
                <div className="flex gap-2">
                  <Select onValueChange={addInvoiceItem}>
                    <SelectTrigger className="h-8 w-64" data-testid="fr-add-invoice-select"><SelectValue placeholder="+ Dari Faktur Vendor (belum lunas)" /></SelectTrigger>
                    <SelectContent>
                      {unpaidInvs.length === 0 ? <SelectItem value="__none" disabled>Tidak ada faktur outstanding</SelectItem> :
                        unpaidInvs.map(i => <SelectItem key={i.id} value={i.id}>{i.number} · {i.partner_name} · sisa {formatIDR(i.balance)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={addExpenseItem} data-testid="fr-add-expense-btn">+ Beban (listrik, internet, ATK…)</Button>
                </div>
              </div>
              {form.items.length === 0 && <div className="text-xs text-slate-500 border border-dashed border-slate-300 rounded p-4 text-center">Belum ada item. Tambahkan dari faktur vendor atau beban manual.</div>}
              {form.items.map((it, idx) => (
                <div key={idx} className={`border rounded-md p-3 mb-2 ${it.type === "purchase_invoice" ? "border-sky-200 bg-sky-50/40" : "border-amber-200 bg-amber-50/40"}`} data-testid={`fr-item-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded font-semibold ${it.type === "purchase_invoice" ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}>
                      {it.type === "purchase_invoice" ? `Faktur Vendor: ${it.invoice_number}` : "Beban Operasional"}
                    </span>
                    <Button variant="ghost" size="sm" className="text-rose-600 h-7" onClick={() => removeItem(idx)} data-testid={`fr-item-del-${idx}`}><Trash size={14} /></Button>
                  </div>
                  <div className="grid grid-cols-12 gap-2">
                    <div className={it.type === "purchase_invoice" ? "col-span-6" : "col-span-5"}>
                      <Label className="text-xs">Deskripsi</Label>
                      <Input value={it.description} onChange={(e) => setItem(idx, { description: e.target.value })}
                        disabled={it.type === "purchase_invoice"} data-testid={`fr-item-desc-${idx}`} />
                    </div>
                    {it.type === "expense" && (
                      <div className="col-span-4">
                        <Label className="text-xs">Akun Beban</Label>
                        <Select value={it.account_code} onValueChange={(v) => { const a = expenseAccs.find(x => x.code === v); setItem(idx, { account_code: v, account_name: a?.name || "" }); }}>
                          <SelectTrigger data-testid={`fr-item-acc-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{expenseAccs.map(a => <SelectItem key={a.code} value={a.code}>{a.code} - {a.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                    {it.type === "expense" && (
                      <div className="col-span-12">
                        <Label className="text-xs">Proyek (opsional)</Label>
                        <ProjectCombobox projects={projects} value={it.project_id || ""} testid={`fr-item-project-${idx}`}
                          onChange={(p) => setItem(idx, { project_id: p?.id || "", project_name: p?.name || "" })} />
                        <div className="text-[10px] text-slate-500 mt-0.5">Tautkan beban ini ke proyek untuk tercatat di batch transaksi proyek. Proyek yang sudah selesai tidak muncul.</div>
                      </div>
                    )}
                    {it.type === "purchase_invoice" && (
                      <div className="col-span-3">
                        <Label className="text-xs">PPh 23 Dipotong (opsional)</Label>
                        <MoneyInput value={it.pph23_withheld} onChange={(e) => setItem(idx, { pph23_withheld: e.target.value })} data-testid={`fr-item-pph23-${idx}`} />
                      </div>
                    )}
                    <div className="col-span-3">
                      <Label className="text-xs">Jumlah (Rp)</Label>
                      <MoneyInput value={it.amount} onChange={(e) => setItem(idx, { amount: e.target.value })} data-testid={`fr-item-amount-${idx}`} />
                    </div>
                  </div>
                  {it.type === "purchase_invoice" && <div className="text-[11px] text-slate-500 mt-1">Vendor: {it.partner_name}</div>}
                </div>
              ))}
              <div className="flex justify-end border-t border-slate-200 pt-2 mt-2">
                <div className="text-sm">Total Pengajuan: <span className="font-bold tabular-nums" data-testid="fr-total">{formatIDR(totalAmount)}</span></div>
              </div>
            </div>
            <div className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-2 border-t border-slate-200 bg-background px-6 py-3">
              <Button className="w-full bg-slate-900" onClick={save} disabled={busy} data-testid="fr-save-btn">{busy ? "Menyimpan…" : "Simpan Draft"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog detail */}
      <Dialog open={!!detailRow} onOpenChange={() => setDetailRow(null)}>
        <DialogContent className="w-[95vw] max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3"><DialogTitle>Detail Pengajuan {detailRow?.number}</DialogTitle></DialogHeader>
          {detailRow && (
            <div className="space-y-3 min-h-0 overflow-y-auto px-6 pt-4 pb-6" data-testid="fr-detail">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div><div className="text-xs text-slate-500">Tanggal</div>{formatDate(detailRow.date)}</div>
                <div><div className="text-xs text-slate-500">Sumber Dana</div>{detailRow.cash_bank_name}</div>
                <div><div className="text-xs text-slate-500">Status</div><span className={`text-xs px-2 py-0.5 rounded font-semibold ${(STATUS_BADGE[detailRow.status] || STATUS_BADGE.draft).cls}`}>{(STATUS_BADGE[detailRow.status] || STATUS_BADGE.draft).label}</span></div>
              </div>
              {detailRow.notes && <div className="text-sm"><span className="text-xs text-slate-500">Catatan: </span>{detailRow.notes}</div>}
              <Table>
                <TableHeader><TableRow><TableHead>Sumber</TableHead><TableHead>Deskripsi</TableHead><TableHead className="text-right">Jumlah</TableHead></TableRow></TableHeader>
                <TableBody>
                  {detailRow.items.map((it, i) => (
                    <TableRow key={i}>
                      <TableCell>{it.type === "purchase_invoice"
                        ? <span className="text-xs px-2 py-0.5 rounded bg-sky-100 text-sky-700 font-semibold">Faktur {it.invoice_number}</span>
                        : <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">Beban {it.account_code}</span>}</TableCell>
                      <TableCell className="text-sm">{it.description}</TableCell>
                      <TableCell className="tabular-nums text-right">{formatIDR(it.amount)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-50 font-bold"><TableCell colSpan={2}>TOTAL</TableCell><TableCell className="tabular-nums text-right">{formatIDR(detailRow.total_amount)}</TableCell></TableRow>
                </TableBody>
              </Table>
              {detailRow.status === "approved" && (
                <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3">
                  <div className="text-xs uppercase font-semibold text-emerald-700 mb-1">Hasil Eksekusi — disetujui oleh {detailRow.approved_by}</div>
                  {(detailRow.execution || []).map((x, i) => (
                    <div key={i} className="text-sm text-emerald-800 flex justify-between"><span>{x.description}</span><span className="font-mono-plex text-xs">{x.ref_no}</span></div>
                  ))}
                </div>
              )}
              <div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
                <Button variant="outline" size="sm" onClick={() => printFr(detailRow)} data-testid="fr-detail-print-btn"><Printer size={14} className="mr-1" />Cetak</Button>
                <Button variant="outline" size="sm" className="text-rose-700" onClick={() => downloadFr(detailRow, "pdf")} data-testid="fr-detail-pdf-btn"><FilePdf size={14} className="mr-1" />PDF</Button>
                <Button variant="outline" size="sm" className="text-emerald-700" onClick={() => downloadFr(detailRow, "xlsx")} data-testid="fr-detail-xlsx-btn"><FileXls size={14} className="mr-1" />Excel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog konfirmasi approve */}
      <Dialog open={!!approveRow} onOpenChange={() => setApproveRow(null)}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3"><DialogTitle>Setujui & Eksekusi {approveRow?.number}?</DialogTitle></DialogHeader>
          {approveRow && (
            <div className="space-y-3 min-h-0 overflow-y-auto px-6 pt-4 pb-6" data-testid="fr-approve-dialog">
              <div className="text-sm text-slate-600">Saat disetujui, sistem akan <b>langsung mengeksekusi</b> {approveRow.items.length} transaksi berikut dari <b>{approveRow.cash_bank_name}</b>:</div>
              <div className="space-y-1">
                {approveRow.items.map((it, i) => (
                  <div key={i} className="flex justify-between text-sm border-b border-slate-100 pb-1">
                    <span>{it.type === "purchase_invoice" ? `💳 Bayar Faktur ${it.invoice_number}` : `🧾 Jurnal Beban: ${it.description}`}</span>
                    <span className="tabular-nums font-semibold">{formatIDR(it.amount)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between font-bold border-t-2 border-slate-900 pt-2"><span>Total Dana Keluar</span><span className="tabular-nums">{formatIDR(approveRow.total_amount)}</span></div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setApproveRow(null)}>Batal</Button>
                <Button className="flex-1 bg-emerald-700 hover:bg-emerald-800" onClick={doApprove} disabled={busy} data-testid="fr-approve-confirm-btn">{busy ? "Mengeksekusi…" : "Setujui & Eksekusi"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog konfirmasi hapus massal riwayat */}
      <Dialog open={bulkDlg} onOpenChange={setBulkDlg}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2" data-testid="fr-bulk-delete-title"><Trash size={18} className="text-rose-600" />Hapus {selected.length} Riwayat Pengajuan?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600">Anda akan menghapus <b>{selected.length} riwayat pengajuan dana</b> secara permanen. Untuk pengajuan yang sudah disetujui, <b>transaksi hasil eksekusi (pembayaran/jurnal) tidak ikut terhapus</b> — hanya riwayat pengajuannya.</div>
            <div className="max-h-28 overflow-y-auto text-xs text-slate-600 space-y-0.5" data-testid="fr-bulk-delete-list">
              {selected.slice(0, 5).map((id) => rows.find((r) => r.id === id)?.number).filter(Boolean).map((n, i) => <div key={i}>• {n}</div>)}
              {selected.length > 5 && <div className="text-slate-500">…dan {selected.length - 5} lainnya</div>}
            </div>
            <div>
              <Label className="text-xs">Ketik <b>HAPUS</b> untuk konfirmasi</Label>
              <Input value={bulkConfirm} onChange={(e) => setBulkConfirm(e.target.value)} placeholder="HAPUS" data-testid="fr-bulk-confirm-input" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setBulkDlg(false)}>Batal</Button>
              <Button className="flex-1 bg-rose-600 hover:bg-rose-700" disabled={bulkConfirm !== "HAPUS" || busy} onClick={bulkDelete} data-testid="fr-bulk-delete-submit">{busy ? "Menghapus…" : `Hapus ${selected.length} Riwayat`}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog rekomendasi AI — hanya usulan, semua butuh konfirmasi user */}
      <Dialog open={reviewDlg} onOpenChange={setReviewDlg}>
        <DialogContent className="w-[95vw] max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3">
            <DialogTitle className="flex items-center gap-2"><Robot size={20} className="text-indigo-600" />Rekomendasi Mata Akun AI</DialogTitle>
            <div className="text-xs text-slate-500 mt-1">AI meninjau item bertipe <b>Beban</b> pada semua draft pengajuan dan mengusulkan mata akun yang lebih mendekati deskripsi. <b>AI tidak mengeksekusi apa pun</b> — Anda harus menekan "Terapkan" untuk masing-masing usulan.</div>
          </DialogHeader>
          <div className="min-h-0 overflow-y-auto px-6 pt-4 pb-6 space-y-3" data-testid="fr-review-content">
            {reviewBusy ? (
              <div className="text-sm text-slate-500 py-8 text-center">AI sedang menganalisis draft pengajuan…</div>
            ) : reviewData.proposals.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center">
                Tidak ada usulan berbeda dari akun saat ini.<br />
                <span className="text-xs">Draft ditinjau: {reviewData.total_draft} · Item beban ditinjau: {reviewData.reviewed_items}</span>
              </div>
            ) : (
              <>
                <div className="text-xs text-slate-500 border-b border-slate-100 pb-2">{reviewData.proposals.length} usulan dari {reviewData.reviewed_items} item beban pada {reviewData.total_draft} draft pengajuan.</div>
                {reviewData.proposals.map((p) => {
                  const key = `${p.fr_id}::${p.item_index}`;
                  const confPct = Math.round(p.confidence * 100);
                  const confColor = confPct >= 70 ? "text-emerald-700 bg-emerald-100" : confPct >= 40 ? "text-amber-700 bg-amber-100" : "text-slate-600 bg-slate-100";
                  return (
                    <div key={key} className="border border-slate-200 rounded-md p-3" data-testid={`fr-review-item-${key}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                            <span className="font-mono-plex font-semibold text-slate-800">{p.fr_number}</span>
                            <span>· {formatDate(p.fr_date)}</span>
                            <span>· <b className="tabular-nums text-slate-800">{formatIDR(p.amount)}</b></span>
                          </div>
                          <div className="text-sm text-slate-800 mb-2">"{p.description}"</div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="border border-slate-200 rounded p-2 bg-slate-50">
                              <div className="text-slate-500 uppercase text-[10px] mb-0.5">Akun saat ini</div>
                              <div className="font-mono-plex">{p.current_code || "—"} {p.current_name || ""}</div>
                            </div>
                            <div className="border border-indigo-300 rounded p-2 bg-indigo-50">
                              <div className="text-indigo-600 uppercase text-[10px] mb-0.5">Usulan AI · <span className={`px-1 rounded ${confColor}`}>{confPct}%</span></div>
                              <div className="font-mono-plex text-indigo-900">{p.suggested_code} {p.suggested_name}</div>
                            </div>
                          </div>
                          <div className="text-xs text-slate-500 italic mt-2">{p.reason}</div>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" className="text-slate-600" onClick={() => setReviewData((d) => ({ ...d, proposals: d.proposals.filter((x) => `${x.fr_id}::${x.item_index}` !== key) }))} data-testid={`fr-review-skip-${key}`}>Lewati</Button>
                        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" disabled={!!applying[key]} onClick={() => applyProposal(p)} data-testid={`fr-review-apply-${key}`}>{applying[key] ? "Menerapkan…" : "Terapkan Usulan"}</Button>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
